import { ActivatedRoute } from '@angular/router';
import { AnhangDataType, AntragMetaData, FormResult, FormValue } from '@eBeihilfe/einreichung-shared/model';
import {
  AnhangDtoModel,
  AntragDtoModel,
  AuthenticationFacade,
  ConfigSettingsService,
  ConverterService,
  EndToEndEncrypter,
  Guid,
  MimeTypeMapReverse,
  ResourceProvider,
  TokenState,
  UploadDataForAntragsSeite,
} from '@ebeihilfe/core';
import { Antrag, Beleg, BelegType, Seite, SeiteFactory } from '../types';
import { EinreichungApiService } from './einreichung-api.service';
import { Injectable, inject } from '@angular/core';
import { LoadingController } from '@ionic/angular/standalone';
import { EinreichungApiService as SharedEinreichungApiService } from '@eBeihilfe/einreichung-shared/services';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { catchError, map } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';

@Injectable()
export class EinreichungService {
  private readonly loadingController = inject(LoadingController);
  private readonly sharedEinreichungApi = inject(SharedEinreichungApiService);
  protected readonly configService = inject(ConfigSettingsService);
  protected readonly converterService = inject(ConverterService);
  protected readonly einreichungsApi = inject(EinreichungApiService);
  protected readonly endToEndEncrypter = inject(EndToEndEncrypter);
  protected readonly seiteFactory = inject(SeiteFactory);
  protected readonly auth = inject(AuthenticationFacade);
  protected readonly route = inject(ActivatedRoute);
  protected readonly resource = inject(ResourceProvider);

  public async *transformToUploadData(
    antrag: Antrag,
  ): AsyncGenerator<[Seite, BelegType, (UploadDataForAntragsSeite & { dataType?: AnhangDataType }) | null]> {
    for (const [index, beleg] of antrag.belege.filter((b) => b.seiten.length > 0).entries()) {
      for await (const uploadDataForSingleSeite of this.transformBeleg(
        antrag.antragsId,
        antrag.timestamp,
        beleg,
        index,
      )) {
        yield uploadDataForSingleSeite;
      }
    }
  }

  public async checkForInteruptedAntrag(): Promise<string | null> {
    const isAuthenticated = await this.auth.isAuthenticated();
    if (!isAuthenticated) {
      return null;
    }

    const { customizing } = this.configService.appSettings;
    if (!customizing.hasUnfinishedAntrage) {
      return null;
    }

    return await this.hasUnfinishedAntragOnServer();
  }

  public async populateAntragFromAntragsInfo(antragId?: string): Promise<Antrag> {
    const unfinishedAntrag: AntragDtoModel = await this.getInteruptedAntragFromServer(antragId);

    if (unfinishedAntrag.tokenState === TokenState.Outdated) {
      throw new Error(_('new_antrag.outdatedEncryption'));
    }

    const firstBelegMandatoryConfig = this.configService.appSettings.customizing.firstBelegMandatoryConfig;
    const antrag: Antrag = new Antrag(firstBelegMandatoryConfig);

    antrag.setDataAfterCreationOrReset(Guid.create(unfinishedAntrag.id), unfinishedAntrag.concurrency);
    antrag.setAntragsType(unfinishedAntrag.antragsArt);
    antrag.containerId = unfinishedAntrag.containerId;
    const { einreichungV3Activated } = this.configService.appSettings;

    if (unfinishedAntrag.metadata) {
      const decryptedData = await this.endToEndEncrypter.decryptFromBase64(unfinishedAntrag.metadata.toString());
      const convertedToString = this.converterService.uIntArrayToString(decryptedData as ArrayBuffer);
      const parsedMetadata = JSON.parse(convertedToString);
      let formattedMetadata: AntragMetaData[] = [];
      if (einreichungV3Activated) {
        formattedMetadata = parsedMetadata.formResults.map((formResult: FormResult) => {
          return new AntragMetaData({
            formResults: [formResult],
          });
        });
      } else {
        formattedMetadata =
          parsedMetadata.formResults
            .find((form: FormResult) => form.templateName === '::LEGACY::')
            ?.results.map((formValue: FormValue) =>
              FormValue.createLegacy(formValue.name, formValue.$type, formValue.value),
            ) || [];
      }
      antrag.setAntragsData(formattedMetadata);
    }

    const belege = new Map<number, AnhangDtoModel[]>();
    unfinishedAntrag.antragsseiteInfo
      .filter((seite: AnhangDtoModel) =>
        einreichungV3Activated ? seite.dataType !== 'form' : seite.dataType !== 'form',
      )
      .sort((a, b) => a.belegNr - b.belegNr)
      .forEach((seite) => {
        const singleBeleg = belege.get(seite.belegNr);
        if (!singleBeleg) {
          belege.set(seite.belegNr, [seite]);
        } else {
          singleBeleg.push(seite);
        }
      });
    for (const belegSeiten of belege.values()) {
      await this.populateBelegFromAntragsInfo(
        antrag,
        belegSeiten.sort((a, b) => a.seitenNr - b.seitenNr),
      );
    }
    return antrag;
  }

  public async populateSeiteFromAntragsInfo(seitenInfo: AnhangDtoModel): Promise<Seite> {
    return this.seiteFactory.CreateFromSeitenInfo(seitenInfo);
  }

  /**
   * Führt den Upload der Belegseiten durch, zeigt währenddessen einen Ladeindikator an.
   *
   * @param antrag - Der Antrag, dessen Belege hochgeladen werden sollen.
   */
  public async uploadPagesWithLoading(antrag: Antrag): Promise<void> {
    const loading = await this.loadingController.create({});
    await loading.present();
    try {
      await this.uploadPages(antrag);
    } finally {
      await loading.dismiss();
    }
  }

  /**
   * Führt den Upload der Belegseiten durch.
   * Verwendet einen Generator, um Uploads schrittweise zu verarbeiten.
   * Fehler bei einzelnen Seiten stoppen nicht den gesamten Prozess.
   *
   * @param antrag - Der Antrag mit den hochzuladenden Belegen.
   */
  private async uploadPages(antrag: Antrag): Promise<void> {
    if (!antrag) {
      return;
    }

    const partialAntrag = {
      antragsId: antrag.antragsId.value,
      timestamp: antrag.timestamp,
      belege: antrag.belege,
    } as unknown as Antrag;

    try {
      const uploadGenerator = this.transformToUploadData(partialAntrag);

      for await (const [seite, belegType, uploadSeite] of uploadGenerator) {
        try {
          if (uploadSeite) {
            // seite has not yet been uploaded
            await firstValueFrom(this.sharedEinreichungApi.uploadAnhang(uploadSeite));
            seite.uploaded = true;
          } else if (seite.id) {
            // seite exists on the server and belegType is updated.
            await firstValueFrom(this.sharedEinreichungApi.updateBelegType(antrag.antragsId, seite.id, belegType));
          }
        } catch (error: unknown) {
          console.error('Error processing seite:', error);
        }
      }
    } catch (error: unknown) {
      console.error('Error during page upload process:', error);
      throw error;
    }
  }

  private getInteruptedAntragFromServer(antragId = ''): Promise<AntragDtoModel> {
    // for now, we only use the first unfinished

    return firstValueFrom(
      (antragId ? this.resource.getApplicationById(antragId) : this.einreichungsApi.getUnfinishedAntraege()).pipe(
        catchError((error) => {
          if (error?.message === 'server.error.missingData') {
            return of([]);
          }
          throw of(error);
        }),
        map((antrags) => {
          if (antragId && antrags) {
            return antrags;
          }

          return antrags?.[0];
        }),
      ),
    );
  }

  private async hasUnfinishedAntragOnServer(): Promise<string | null> {
    const antrage: AntragDtoModel = await this.getInteruptedAntragFromServer();
    return antrage != null ? antrage.id : null;
  }

  private async *transformBeleg(
    antragsId: Guid,
    antragsCreatedOn: string,
    beleg: Beleg,
    belegIndex: number,
  ): AsyncGenerator<[Seite, BelegType, (UploadDataForAntragsSeite & { dataType?: AnhangDataType }) | null]> {
    for (const [index, seite] of beleg.seiten.entries()) {
      const result = await this.transformSinglePage(
        seite,
        index,
        belegIndex,
        antragsId,
        antragsCreatedOn,
        beleg.belegType,
      );
      yield [seite, beleg.belegType, result];
    }
  }

  private async encryptBlob(file: Blob): Promise<Blob> {
    const uintArray = await this.converterService.blobToUint8Array(file);
    const page = (await this.endToEndEncrypter.encryptToHex(uintArray)) || '';
    return new Blob([this.converterService.hexStringToUint8Array(page || '')], { type: file.type });
  }

  private async transformSinglePage(
    seite: Seite,
    seitenIndex: number,
    belegIndex: number,
    antragsId: Guid,
    antragsTimestamp: string,
    belegType: BelegType,
  ): Promise<(UploadDataForAntragsSeite & { dataType?: AnhangDataType }) | null> {
    // Skip if already uploaded
    if (seite.uploaded) {
      return null;
    }

    const file = await seite.getFile();
    const pageData = await this.encryptBlob(file);
    const dataType = MimeTypeMapReverse.get(file.type) as AnhangDataType;

    return {
      antragId: antragsId.toString(),
      pageData,
      belegNumber: (belegIndex + 1).toString(10),
      seiteNumber: (seitenIndex + 1).toString(10),
      thumb: seite.thumb,
      timestamp: antragsTimestamp,
      belegtyp: belegType.toString(10),
      dataType,
    };
  }

  private async populateBelegFromAntragsInfo(antrag: Antrag, seitenToAddToBeleg: AnhangDtoModel[]): Promise<void> {
    const beleg = antrag.addBeleg();
    beleg.belegType = seitenToAddToBeleg[0]?.belegtyp as BelegType;

    for (const seitenInfo of seitenToAddToBeleg.sort((a, b) => a.seitenNr - b.seitenNr)) {
      const seite = await this.populateSeiteFromAntragsInfo(seitenInfo);
      beleg.addSeite(seite);
    }
  }
}
